import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import type { AssignablePerson, BoardColumn, Grant, Priority, WorkItemCard, WorkItemType } from '../lib/types';
import { WORK_ITEM_TYPE_LABELS } from '../lib/types';
import { initials } from './BoardCard';
import { PriorityBadge } from '../components/Badges';
import { GitlabPanel } from './GitlabPanel';
import { TaskTimePanel } from './TaskTimePanel';
import { CommentsPanel } from './CommentsPanel';
import { DocumentList } from '../components/DocumentList';
import { SlaPanel } from './SlaPanel';
import { CategoryField } from './CategoryField';
import { AssigneePicker, AssigneeStack } from '../components/Assignees';
import { WORK_STAGES, WORK_STAGE_LABELS, type WorkStage } from '../lib/types';
import { DateCell } from '../components/DateCell';
import { ResolutionPanel } from './ResolutionPanel';
import { TestPanel } from './TestPanel';
import { formatDate, formatDateTime } from '../lib/dates';
import { changedFields } from '../lib/workItemPatch';
import { Linkified, LinksIn } from '../components/Linkified';

interface Props {
  itemId: string;
  companyId: string;
  /**
   * OPTIONAL, because this drawer is opened from four views now and only the board holds these
   * already.
   *
   * The board passes what it has, which saves a round trip and keeps one source on that page.
   * The plan's timeline and diagram pass nothing and the API supplies them — which is the
   * whole reason they no longer need an editor of their own.
   */
  people?: AssignablePerson[];
  columns?: BoardColumn[];
  onClose: () => void;
  onChanged: (message?: string) => void | Promise<void>;
  onDeleted: () => void | Promise<void>;
}

export function WorkItemDrawer({
  itemId,
  companyId,
  people: givenPeople,
  columns: givenColumns,
  onClose,
  onChanged,
  onDeleted,
}: Props) {
  const { session } = useSession();
  const [item, setItem] = useState<WorkItemCard | null>(null);
  /** The item as the API last gave it, which is what "changed" is measured against. */
  const [loaded, setLoaded] = useState<WorkItemCard | null>(null);
  /**
   * The same value, readable without making `reload` depend on it — a dependency there would
   * rebuild the callback on every save and restart the effect that calls it.
   */
  const loadedRef = useRef<WorkItemCard | null>(null);
  /** Which project it belongs to — the card carries only an id, so the name comes with it. */
  const [project, setProject] = useState<{
    id: string;
    name: string;
    code: string;
    companyId: string;
    billable: boolean;
  } | null>(null);
  const ask = useAsk();
  const [caps, setCaps] = useState<Record<string, Grant>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  /** What the API supplied, used wherever the opener did not pass its own. */
  const [fetched, setFetched] = useState<{
    people: AssignablePerson[];
    columns: BoardColumn[];
  } | null>(null);

  /**
   * FETCHING THE TASK AGAIN, which this drawer could not do.
   *
   * It read the item once on mount and never again, and several panels below it write straight
   * to the API — the service level, the category, the resolution, the test result. Each told the
   * BOARD it had changed and the board reloaded behind the drawer, while the drawer itself went
   * on showing what it had read when it opened. Setting the impact and watching the panel say
   * the old one until you closed and reopened the task is exactly that.
   *
   * UNSAVED TYPING SURVIVES IT. Anything the reader has changed and not saved is re-applied
   * over the fresh copy, so setting the service level halfway through writing a description
   * does not throw the description away. Measured against `loaded`, which is what "unsaved"
   * means everywhere else here.
   */
  const reload = useCallback(async () => {
    try {
      const result = await api.get<{
        item: WorkItemCard;
        project: { id: string; name: string; code: string; companyId: string; billable: boolean };
        capabilities: Record<string, Grant>;
        people: AssignablePerson[];
        columns: BoardColumn[];
      }>(`/work-items/${itemId}`);

      setItem((current) => {
        const baseline = loadedRef.current;
        if (!current || !baseline) return result.item;

        const unsaved: Partial<WorkItemCard> = {};
        for (const key of Object.keys(result.item) as (keyof WorkItemCard)[]) {
          if (JSON.stringify(current[key]) !== JSON.stringify(baseline[key])) {
            unsaved[key] = current[key] as never;
          }
        }
        return { ...result.item, ...unsaved };
      });

      /* Kept so a save can send what CHANGED rather than everything. See `save`. */
      setLoaded(result.item);
      setProject(result.project);
      setCaps(result.capabilities);
      setFetched({ people: result.people, columns: result.columns });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that item');
    }
  }, [itemId]);

  /* The ref shadows the state so `reload` can read the baseline without depending on it. */
  useEffect(() => {
    loadedRef.current = loaded;
  }, [loaded]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * Told what a child panel changed: refetch this task, THEN tell the board.
   *
   * In that order, because the board's reload is what re-renders the card behind the drawer and
   * the drawer's own copy is the one the reader is looking at.
   */
  const refresh = useCallback(async () => {
    await reload();
    await onChanged();
  }, [reload, onChanged]);

  const people = givenPeople ?? fetched?.people ?? [];
  const columns = givenColumns ?? fetched?.columns ?? [];

  function edit<K extends keyof WorkItemCard>(field: K, value: WorkItemCard[K]) {
    setItem((current) => (current ? { ...current, [field]: value } : current));
    setDirty(true);
  }

  /**
   * CHOOSING FROM A LIST SAVES ITSELF. TYPING NEEDS SAVE.
   *
   * That is the whole rule, and it is drawn where it is because the two acts are different.
   * Picking "Bug" from a dropdown is a decision somebody has finished making — there is no
   * half-chosen state, and asking them to confirm it twice is asking them to do the same thing
   * twice. A title or a description is not finished until they stop typing, and saving each
   * keystroke would be both wrong and noisy.
   *
   * Save stays for the typed fields, and still works for everything: a picker that saved itself
   * simply leaves nothing for it to send.
   *
   * OPTIMISTIC, WITH THE OLD VALUE PUT BACK IF IT FAILS. The select shows the new choice at
   * once because that is what was clicked; a refusal — a client trying to set a stage, a
   * workflow that forbids a move — restores what was there and says why. A control that
   * silently keeps a value the server rejected is how two screens start disagreeing.
   */
  async function commit<K extends keyof WorkItemCard>(field: K, value: WorkItemCard[K], body?: Record<string, unknown>) {
    if (!item || !loaded) return;
    const previous = item[field];
    if (previous === value) return;

    setItem((current) => (current ? { ...current, [field]: value } : current));
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/work-items/${item.id}`, body ?? { [field]: value });
      /* The baseline moves too, so this field is no longer part of what Save would re-send. */
      setLoaded((current) => (current ? { ...current, [field]: value } : current));
      /*
        A stage can move the card — "Development done" to In review, "Done" to Done — so the
        task is read back and the Status field shows where it went. `reload` keeps anything
        typed and not yet saved.
      */
      if (field === 'stage') await refresh();
      else await onChanged();
    } catch (err) {
      setItem((current) => (current ? { ...current, [field]: previous } : current));
      setError(err instanceof ApiError ? err.message : 'Could not save that');
    } finally {
      setSaving(false);
    }
  }

  /**
   * The same, for the column — which is a MOVE and goes to its own endpoint, where the
   * workflow's allowed transitions live. See `save`.
   */
  async function commitStatus(statusId: string) {
    if (!item || !loaded || statusId === item.statusId) return;
    const previous = item.statusId;

    setItem((current) => (current ? { ...current, statusId } : current));
    setError(null);
    setSaving(true);
    try {
      await api.put(`/work-items/${item.id}/move`, { statusId, position: 0 });
      setLoaded((current) => (current ? { ...current, statusId } : current));
      await onChanged();
    } catch (err) {
      setItem((current) => (current ? { ...current, statusId: previous } : current));
      setError(err instanceof ApiError ? err.message : 'Could not move it');
    } finally {
      setSaving(false);
    }
  }


  /**
   * ONLY WHAT CHANGED, and that is a correction of something that had gone quietly wrong.
   *
   * This used to post every field on every save, which broke in two ways once the permissions
   * were split. A customer pressing Save after fixing a typo sent `points` and `dueDate`
   * along with the title — fields they may not set — and the API refused the whole save with
   * a message about sizing work they had not touched. And `stage` was simply MISSING from the
   * list, so choosing a stage marked the drawer dirty, saved cleanly, and changed nothing:
   * the field somebody had just set was the one field never sent.
   *
   * A diff fixes both at once. Nothing untouched is mentioned, so no gate is tripped by a
   * field that is not being changed, and adding a field to the drawer cannot leave it out of
   * the save by omission.
   */
  async function save() {
    if (!item || !loaded) return;
    setSaving(true);
    setError(null);
    try {
      const patch = changedFields(loaded, item);

      /* Nothing to say: a save that changed nothing should not be a request at all. */
      if (Object.keys(patch).length === 0 && item.statusId === loaded.statusId) {
        setDirty(false);
        setSaving(false);
        return;
      }

      /**
       * A COLUMN CHANGE IS A MOVE, not a field edit, and goes to the move endpoint — the same
       * one dragging a card uses. That is deliberate rather than tidy: moving is where the
       * workflow's allowed transitions are enforced and where a blocked column asks for a
       * reason, and routing it through PATCH would be a second way in that skips both.
       *
       * Position 0 puts it at the top of the column it lands in; the board is ordered by hand
       * and the top is the least surprising place for something that has just moved.
       */
      if (item.statusId !== loaded.statusId && item.statusId) {
        await api.put(`/work-items/${item.id}/move`, { statusId: item.statusId, position: 0 });
      }

      if (Object.keys(patch).length > 0) await api.patch(`/work-items/${item.id}`, patch);
      /* The baseline moves with it, or the next save re-sends a diff against stale values. */
      setLoaded(item);
      setDirty(false);
      await onChanged(`${item.key} saved.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that item');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!item) return;
    const sure = await ask.confirm({
      title: `Delete ${item.key}?`,
      body: 'This cannot be undone. Its comments, time and documents go with it.',
      confirmLabel: 'Delete it',
      danger: true,
    });
    if (!sure) return;
    setSaving(true);
    try {
      await api.del(`/work-items/${item.id}`);
      await onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that item');
      setSaving(false);
    }
  }

  const editable = !!item?.editable && caps['work.write'] !== 'none';
  /**
   * What it will take, and when — a different question from whether they may edit at all.
   *
   * A customer says what is wanted and how badly; we say what it costs and when it fits. These
   * controls are shown to them, read-only, rather than hidden: the points and the date are
   * theirs to KNOW, and a field that vanishes looks like something is being kept from them.
   */
  const canPlan = editable && caps['work.plan'] !== 'none';

  /**
   * DECIDED PER FIELD, NOT PER DRAWER — which is the correction this pass makes.
   *
   * `editable` was doing two jobs. It answered "may you change the wording of this task", which
   * is what it means, and it was also standing in for "should this drawer be a form at all".
   * That produced the two complaints: a client admin got the whole form with most of it greyed
   * out, because they hold `work.write` and almost nothing else; and a plain client could not
   * put their own person on a task they had not raised, because assignment was behind a gate
   * about authorship.
   *
   * So each control now asks its own question, and everything nobody may change is stated in
   * one block instead. Two of these are deliberately NOT `&& editable`:
   *
   *   `canAssign`  — whose name goes on the work is `work.assign`, and a customer holds it for
   *                  their own people on any of their company's work. See the API, which was
   *                  fixed to agree.
   *   `canSetSla`  — who is affected and how badly is the customer's report about their own
   *                  business, not an edit to ours.
   */
  const canAssign = caps['work.assign'] !== 'none';
  const canSetSla = caps['work.sla'] !== 'none';
  /**
   * Adding detail to what was asked for. Its own capability, and NOT `&& editable`: the detail
   * that helps turns up late and rarely from whoever first logged it. The title stays behind
   * `work.write` — that is naming our work, this is telling us about theirs.
   *
   * It was also stopped by a running sprint's scope freeze. Sprints, and the freeze, were
   * retired on 2026-10-08.
   */
  const canDescribe = caps['work.describe'] !== 'none';
  /**
   * Bug or feature — warranty against billable, so not on `work.write`. Their LEAD holds it and
   * everybody else on their side does not; see the capability.
   */
  const canClassify = caps['work.classify'] !== 'none';
  /**
   * DELETING IS NOT EDITING, so it does not ride on `editable`.
   *
   * `editable` means `work.write` — which a client admin holds at full, so the drawer offered
   * them a Delete button on our delivery work. Its own capability now, held by our people and
   * neither client role; see `work.delete`. `item.editable` stays in the test for the rest of
   * what it carries: ownership on an `own` grant.
   */
  const canRemove = caps['work.delete'] !== 'none' && !!item?.editable;
  const canMove = caps['work.move'] !== 'none' && !!item?.editable;
  const canStage = caps['work.stage'] !== 'none' && editable;
  /** Whether the matrix owns the priority, in which case nobody types it. */
  const derivedPriority = !!item?.impact && !!item?.urgency;

  const column = columns.find((c) => c.id === item?.statusId);

  return (
    <div className="drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={item ? item.key : 'Work item'}>
        {!item ? (
          <div className="empty">{error ?? 'Loading…'}</div>
        ) : (
          <>
            <div className="drawer-head">
              <div className="row-between">
                <div className="row wrap" style={{ gap: 9 }}>
                  <span className="key">{item.key}</span>
                  {/*
                    Company › project, and a way into either. A task opened from Issues, My work
                    or search could say nothing about where it lived — the card carries a project
                    id and no name, so there was nothing to show and nowhere to click. The
                    company is the one people actually wanted: from a task, its dashboard was
                    three hops away.
                  */}
                  {project && (
                    <span className="crumbs" style={{ fontSize: 12 }}>
                      <Link to={`/companies/${project.companyId}`} className="muted">
                        {session?.companies.find((c) => c.id === project.companyId)?.name ?? 'Company'}
                      </Link>
                      <span aria-hidden>›</span>
                      <Link to={`/projects/${project.id}`} className="muted">
                        {project.name}
                      </Link>
                    </span>
                  )}
                  {column && (
                    <span className="badge">
                      <span className="dot" style={{ background: column.color }} /> {column.label}
                    </span>
                  )}
                  {item.blockedSince && <span className="badge badge-accent">blocked</span>}
                  {item.closedAt ? (
                    <span className="badge">closed</span>
                  ) : item.completedAt ? (
                    <span className="badge badge-ok">resolved</span>
                  ) : null}
                  {item.reopenCount > 0 && <span className="badge badge-warn">reopened {item.reopenCount}×</span>}
                </div>
                <button type="button" className="btn btn-quiet btn-sm" onClick={onClose} aria-label="Close">
                  ✕
                </button>
              </div>
              {editable ? (
                <textarea
                  rows={2}
                  value={item.title}
                  onChange={(e) => edit('title', e.target.value)}
                  className="ui-font"
                  style={{ fontSize: 19, fontWeight: 700, marginTop: 10, border: 'none', background: 'transparent', padding: 0 }}
                />
              ) : (
                <h2 style={{ marginTop: 10 }}>{item.title}</h2>
              )}
            </div>

            <div className="drawer-body">
              {error && <div className="callout">{error}</div>}
              {!editable && (
                /*
                  "Read-only" would be untrue — impact, urgency, assignment and comments are all
                  still open — so it says only what it describes: somebody reading work that is
                  not theirs to change.
                */
                <div className="callout">
                  You have read-only access to this item.
                </div>
              )}

              {/*
                EVERYTHING THIS READER MAY NOT CHANGE, IN ONE BLOCK.

                It was a form with its controls disabled, then a form with its controls replaced
                by loose text — and the second was not much better than the first. A column of
                bare labels with a sentence of explanation under half of them is a lot of words
                for a handful of facts, and it read as though something had failed to load.

                So the whole read-only half is one card of label-and-value rows: the same shape
                the "Raised by / Raised on" block below has always used, which is the pattern
                this drawer already had for exactly this job.

                The three "this is ours to change" sentences are gone with it. They said the same
                thing three times, and the callout at the top of the drawer says it once.
              */}
              {/*
                EVERYTHING THIS READER MAY NOT CHANGE, IN ONE BLOCK — AND ONLY THAT.

                Built row by row rather than all-or-nothing, which is the fix for two things at
                once. A client admin used to get the whole form with most of it greyed out,
                because they hold `work.write` and almost nothing else; and a customer's view
                was either a form or a wall of text, with no way to keep the one or two controls
                they genuinely hold.

                So a field appears here when its own capability is absent, and as a control when
                it is not. Nothing is both, and nothing is a disabled control any more.
              */}
              {(() => {
                const stated: React.ReactNode[] = [];
                if (!canClassify) stated.push(<Row key="type" label="Type">{WORK_ITEM_TYPE_LABELS[item.type]}</Row>);
                if (!canPlan || derivedPriority) {
                  stated.push(
                    <Row key="priority" label={derivedPriority ? 'Priority (from the matrix)' : 'Priority'}>
                      <PriorityBadge priority={item.priority} />
                    </Row>,
                  );
                }
                if (!canMove) {
                  stated.push(
                    <Row key="status" label="Status">
                      {column ? (
                        <span className="status-pill" style={{ background: column.color }}>
                          {column.label}
                        </span>
                      ) : (
                        <span className="dim">not on the board</span>
                      )}
                    </Row>,
                  );
                }
                if (!canStage) {
                  stated.push(
                    <Row key="stage" label="Stage">
                      {item.stage ? (
                        <span className={`badge stage-${item.stage}`}>{WORK_STAGE_LABELS[item.stage]}</span>
                      ) : (
                        <span className="dim">not said yet</span>
                      )}
                    </Row>,
                  );
                }
                if (!canPlan) {
                  stated.push(
                    <Row key="category" label="Category">
                      {item.category ? item.category.path : <span className="dim">uncategorised</span>}
                    </Row>,
                  );
                }
                if (!canAssign) {
                  stated.push(
                    <Row key="assigned" label="Assigned to">
                      {item.assignees.length > 0 ? (
                        <span className="row" style={{ gap: 7 }}>
                          <AssigneeStack people={item.assignees} />
                          {item.assignees.map((person) => person.name).join(', ')}
                        </span>
                      ) : (
                        <span className="dim">unassigned</span>
                      )}
                    </Row>,
                  );
                }
                if (!canPlan) {
                  stated.push(
                    <Row key="points" label="Points">
                      {item.points === null ? <span className="dim">not sized</span> : `${item.points} pts`}
                    </Row>,
                    <Row key="dates" label="Dates">
                      {item.startDate || item.dueDate ? (
                        <span className="row" style={{ gap: 6 }}>
                          <span>{item.startDate ? formatDate(item.startDate) : '—'}</span>
                          <span className="dim">→</span>
                          <span style={item.overdue ? { color: 'var(--accent)', fontWeight: 600 } : undefined}>
                            {item.dueDate ? formatDate(item.dueDate) : '—'}
                          </span>
                        </span>
                      ) : (
                        <span className="dim">no dates yet</span>
                      )}
                    </Row>,
                  );
                }

                if (stated.length === 0) return null;
                return (
                  /* `mb-2` to match the 18px every other block in this drawer sits on. */
                  <div className="card card-pad mb-2" style={{ background: 'var(--surface-sunken)' }}>
                    <div className="stack-sm">{stated}</div>
                  </div>
                );
              })()}

              {(canClassify || (canPlan && !derivedPriority)) && (
                <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                  {canClassify && (
                  <label className="field grow">
                    <span className="field-label">Type</span>
                    <select
                      value={item.type}
                      disabled={saving}
                      onChange={(e) => void commit('type', e.target.value as WorkItemType)}
                    >
                      {(Object.keys(WORK_ITEM_TYPE_LABELS) as WorkItemType[]).map((key) => (
                        <option key={key} value={key}>
                          {WORK_ITEM_TYPE_LABELS[key]}
                        </option>
                      ))}
                    </select>
                  </label>
                  )}

                  {canPlan && !derivedPriority && (
                    <label className="field grow">
                      <span className="field-label">Priority</span>
                      <select
                        value={item.priority}
                        disabled={saving}
                        onChange={(e) => void commit('priority', e.target.value as Priority)}
                      >
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                        <option value="none">No priority</option>
                      </select>
                    </label>
                  )}
                </div>
              )}

              {/*
                WHICH COLUMN IT IS IN, changeable from here.
                
                It was a read-only badge in the header, because on the board you move a card by
                dragging it. The plan has no columns to drag between, so its own editor grew a
                "Mark done" button and nothing else — you could not send something back to
                Backlog from the timeline at all. A picker serves both: drag on the board if you
                like, choose here if you are anywhere else.
                
                `work.move` rather than `work.write`: moving work between columns is its own
                question, and the same one dragging asks.
              */}
              {columns.length > 0 &&
                (canMove ? (
                  <label className="field">
                    <span className="field-label">Status</span>
                    <select
                      value={item.statusId ?? ''}
                      disabled={saving}
                      onChange={(event) => event.target.value && void commitStatus(event.target.value)}
                    >
                      {!item.statusId && <option value="">No column</option>}
                      {columns.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null)}

              {/*
                Where it has got to inside "in progress".

                A picker for us, a badge for the customer — not a disabled select, which reads
                as something broken rather than something that is not theirs. They are meant to
                READ this; it is the answer to "what is actually happening".
              */}
              {canStage ? (
                <label className="field">
                  <span className="field-label">Stage</span>
                  <select
                    value={item.stage ?? ''}
                    disabled={saving}
                    onChange={(event) => void commit('stage', (event.target.value || null) as WorkStage | null)}
                  >
                    <option value="">Not said yet</option>
                    {WORK_STAGES.map((stage) => (
                      <option key={stage} value={stage}>
                        {WORK_STAGE_LABELS[stage]}
                      </option>
                    ))}
                  </select>
                  <span className="field-hint">Shown to the customer. Ours to set.</span>
                </label>
              ) : null}

              {/*
                `canPlan`, not `editable` — otherwise a client admin got BOTH, once as a row in
                the card above and again as a heading here. They hold `work.write` and not
                `work.plan`, which is exactly the gap the two gates left between them.
              */}
              {canPlan && (
                <CategoryField item={item} companyId={companyId} editable onChanged={() => void refresh()} />
              )}

              {/*
                Hidden for a reader who cannot change it — their copy is a row in the card
                above. A picker they can look at and not use is the thing this whole pass was
                about removing.
              */}
              {canAssign && (
              <div className="field">
                <span className="field-label">
                  {item.assignees.length > 1 ? `Assigned to ${item.assignees.length} people` : 'Assigned to'}
                </span>
                <AssigneePicker
                  value={item.assignees}
                  people={people}
                  /* The block only renders when the capability is held, so nothing else to check. */
                  disabled={saving}
                  grant={caps['work.assign']}
                  /**
                   * COMMITS ITSELF, like every other picker here — and it has to.
                   *
                   * Save only renders for somebody who may edit the task's wording. A client can
                   * now put their own person on work they did not raise, which is exactly a task
                   * they may NOT reword — so leaving this on the Save button would have given
                   * them a picker they could use and no way to keep what they chose.
                   *
                   * `assigneeIds` is what the API takes; `assignees` is what the card holds.
                   */
                  onChange={(next) =>
                    void commit('assignees', next, { assigneeIds: next.map((person) => person.id) })
                  }
                />
                {caps['work.assign'] === 'own' && (
                  <span className="field-hint">Your own people. Ours are ours to assign.</span>
                )}
              </div>
              )}

              {/*
                Points, start and due. `canPlan`, for the same reason as the category above: a
                reader who cannot set them has them in the card, and gating on `editable` showed
                a client admin both copies.
              */}
              {canPlan && (
              <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                {canPlan ? (
                  <label className="field grow">
                    <span className="field-label">Points</span>
                    <input
                      type="number"
                      min={0}
                      max={999}
                      value={item.points ?? ''}
                      onChange={(e) => edit('points', e.target.value === '' ? null : Number(e.target.value))}
                    />
                  </label>
                ) : (
                  /* The size, said rather than offered. A greyed spinner is still a spinner. */
                  <div className="field grow">
                    <span className="field-label">Points</span>
                    <span className="mono">{item.points ?? <span className="dim">—</span>}</span>
                  </div>
                )}
                {/*
                  A START AS WELL AS A DUE DATE.
                  
                  Only the plan had this, which is a large part of why the plan needed an editor
                  of its own — a task opened from the board could not be given the span the
                  timeline draws. Same field, same task, now in one place.
                */}
                <div className="field grow">
                  <span className="field-label">Start</span>
                  <DateCell value={item.startDate} editable={canPlan} onChange={(next) => edit('startDate', next)} />
                </div>
                <div className="field grow">
                  <span className="field-label">Due date</span>
                  {/* Reads dd/mm/yyyy like everywhere else; the picker appears on click. */}
                  <DateCell
                    value={item.dueDate}
                    editable={canPlan}
                    emphasis={item.overdue}
                    onChange={(next) => edit('dueDate', next)}
                  />
                </div>
              </div>
              )}

              {/*
                TYPED, so this one keeps the Save button — see `commit`. A textarea that saved
                on every keystroke would be both wrong and noisy.
              */}
              {canDescribe ? (
                <label className="field">
                  <span className="field-label">Description</span>
                  <textarea rows={4} value={item.description} onChange={(e) => edit('description', e.target.value)} />
                  <LinksIn text={item.description} />
                </label>
              ) : (
                /*
                  SHOWN EVEN WHEN EMPTY, and drawn as the box it would be if you could type in
                  it. Leaving it out when there is nothing written was a mistake: a reader then
                  cannot tell a task with no description from one whose description failed to
                  load, and the panel changes shape depending on what happens to be filled in.
                  An empty field is a fact — somebody has not written one.

                  Its own block rather than a row in the card above, because a description is
                  prose and can run to paragraphs where everything in that card is a word or two.
                */
                <div className="field mb-2">
                  <span className="field-label">Description</span>
                  <div className="stated-box">
                    {item.description ? (
                      /* `pre-wrap` so paragraphs somebody typed survive being read back. */
                      <span style={{ whiteSpace: 'pre-wrap' }}><Linkified text={item.description} /></span>
                    ) : (
                      <span className="dim">Nothing written yet</span>
                    )}
                  </div>
                </div>
              )}

              {item.blockedSince && (
                <label className="field">
                  <span className="field-label">Why it is blocked</span>
                  {/*
                    `canPlan`, not `editable`. Why our work is held up is our note — the API
                    treats it as a planning field, and a live textarea that answered 403 on
                    save is the exact mismatch these permissions are meant to remove.
                  */}
                  <textarea
                    rows={2}
                    disabled={!canPlan}
                    value={item.blockedReason}
                    onChange={(e) => edit('blockedReason', e.target.value)}
                  />
                  <span className="field-hint">Blocked since {formatDateTime(item.blockedSince)}.</span>
                </label>
              )}

              <div className="card card-pad" style={{ background: 'var(--surface-sunken)' }}>
                <div className="stack-sm">
                  <Row label="Raised by">
                    {item.reporter ? (
                      <span className="row" style={{ gap: 7 }}>
                        <span className="avatar">{initials(item.reporter.name)}</span>
                        {item.reporter.name}
                      </span>
                    ) : (
                      <span className="dim">unknown</span>
                    )}
                  </Row>
                  <Row label="Raised on">{formatDate(item.createdAt)}</Row>
                  {item.startedAt && <Row label="Started">{formatDate(item.startedAt)}</Row>}
                  {item.completedAt && <Row label="Completed">{formatDate(item.completedAt)}</Row>}
                </div>
              </div>

              {/*
                The tester's two answers, above the resolution: passing IS the resolution most
                of the time, and offering "Mark resolved" first would have them doing the same
                thing twice by different names.
              */}
              {caps['work.move'] !== 'none' && !item.closedAt && (
                <TestPanel item={item} columns={columns} onChanged={() => void refresh()} />
              )}

              <ResolutionPanel
                item={item}
                editable={editable}
                canResolve={caps['work.move'] !== 'none'}
                onChanged={() => void refresh()}
              />

              {/*
                `work.sla`, NOT `editable`.
                
                Impact and urgency are the customer's two questions — who is affected and how
                badly — so a client may answer them on any of their company's work, including
                a task one of their colleagues raised. `editable` would have limited a plain
                client to the requests they personally logged, which is the wrong test: the
                person watching the fault spread on Monday is rarely whoever logged it Friday.
              */}
              <SlaPanel item={item} editable={canSetSla} onChanged={() => void refresh()} />

              {/* The evidence of what was tested belongs on the task it was tested against. */}
              <DocumentList scope="work-items" id={item.id} canUpload={caps['work.write'] !== 'none'} compact />

              {/*
                `?reply=1` comes from the button in a mention email. It lands them in the box
                rather than beside it — a "Reply" that opens the page and leaves you hunting
                for where to type has saved nobody anything.
              */}
              <CommentsPanel
                itemId={item.id}
                canComment={caps['work.write'] !== 'none'}
                focusReply={new URLSearchParams(window.location.search).get('reply') === '1'}
              />

              {/*
                The timer lives on the task now, not only on the timesheet — see the panel.
                `time.log` gates the button: a customer still sees what has been booked, which
                is theirs to know, and has no way to book any.
              */}
              <TaskTimePanel
                itemId={item.id}
                projectId={item.projectId}
                itemKey={item.key}
                canLog={caps['time.log'] !== 'none'}
                billable={item.billable ?? project?.billable ?? true}
                /*
                  WHETHER TIME ON IT IS CHARGED sits with the time, not among the planning fields:
                  it is a question about the hours, and this is where the hours are. Ours to set,
                  like the stage.
                */
                billing={
                  canStage && project
                    ? {
                        task: item.billable ?? null,
                        project: project.billable,
                        saving,
                        onChange: (value) => void commit('billable', value),
                      }
                    : undefined
                }
              />

              {/*
                HOW THE WORK IS BUILT, WHICH IS NOT THE CUSTOMER'S BUSINESS.
                
                This panel rendered for everybody, so a client opening a task was shown the
                branch name, the merge request, its pipeline status and who approved it — a
                customer reading "pipeline failed" has been handed a fact they can neither act
                on nor interpret. The API refuses them too; this is so they are not shown an
                empty box with an error in it.
              */}
              {caps['change.merge'] !== 'none' && (
                <GitlabPanel
                  itemId={item.id}
                  projectId={item.projectId}
                  itemKey={item.key}
                  onTaskMoved={() => void refresh()}
                />
              )}

              {/*
                The footer shows for anybody with something Save can send — which is now more
                than "may they reword it". A client who may only add detail to the description
                needs the button as much as anybody; without this they could type into the box
                and have no way to keep it.

                Delete is behind `work.delete`, not `editable`. Adding a paragraph, rewording the
                task and destroying it are three different acts, and the middle one was standing
                in for the last.
              */}
              {(editable || canDescribe) && (
                <div className="row-between mt-2">
                  {canRemove ? (
                    <button type="button" className="btn btn-danger" onClick={remove} disabled={saving}>
                      Delete
                    </button>
                  ) : (
                    <span />
                  )}
                  <div className="row">
                    <button type="button" className="btn btn-quiet" onClick={onClose}>
                      Close
                    </button>
                    <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !dirty}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="row-between">
      <span className="muted">{label}</span>
      <span>{children}</span>
    </div>
  );
}
